import {
  JobApplicationFormData,
  JobItem,
  MediaItem,
  SchemeProsConsPayload,
  SchemeProsConsResponse,
  TextFormattingTag,
} from "@/type/commonTypes";
import { apiPost } from "./apiClient";
import { getNewsMedia } from "@/lib/prismaController";

export function normalizeBigInt<T>(obj: T): T {
  return JSON.parse(
    JSON.stringify(obj, (_, value) =>
      typeof value === "bigint" ? value.toString() : value,
    ),
  );
}

export const calculateReturn = (
  seriesPoints: Highcharts.Point[],
  minRange: number,
  maxRange: number,
): number => {
  if (!Array.isArray(seriesPoints) || seriesPoints.length === 0) {
    return 0;
  }

  const startIndex = seriesPoints.findIndex((point) => point.x >= minRange);
  let endIndex = seriesPoints.findIndex((point) => point.x >= maxRange);

  if (seriesPoints.length) {
    endIndex = seriesPoints.length - 1;
  }

  const startValue = startIndex !== -1 ? seriesPoints[startIndex].y : null;
  const endValue = endIndex !== -1 ? seriesPoints[endIndex]?.y : null;

  if (
    typeof startValue === "number" &&
    typeof endValue === "number" &&
    startValue !== 0
  ) {
    const overallReturn = (endValue - startValue) / startValue;
    return overallReturn * 100;
  }

  return 0;
};

export function average(arr: number[]): number {
  return arr.reduce((a, b) => a + b, 0) / arr.length;
}

export function standardDeviation(arr: number[]): number {
  const avg = average(arr);
  const variance =
    arr.reduce((sum, val) => sum + Math.pow(val - avg, 2), 0) /
    (arr.length - 1);
  return Math.sqrt(variance);
}

export function coefficientOfVariation(arr: number[]): number {
  const avg = average(arr);
  return avg !== 0 ? standardDeviation(arr) / avg : 0;
}

export function hitRatio(arr: number[]): number {
  const positiveCount = arr.filter((val) => val > 0).length;
  return (positiveCount / arr.length) * 100;
}

export function maxDrawdown(arr: number[]): number {
  let peak = arr[0];
  let maxDD = 0;
  let cumulative = 0;

  const cumValues: number[] = arr.map((r) => {
    cumulative = (1 + cumulative / 100) * (1 + r / 100) - 1;
    return cumulative * 100;
  });

  for (const value of cumValues) {
    if (value > peak) peak = value;
    const drawdown = ((peak - value) / (peak === 0 ? 1 : peak)) * 100;
    if (drawdown > maxDD) maxDD = drawdown;
  }

  return maxDD;
}

export function getScore(data: number[]): number {
  const hit = hitRatio(data); //50
  const cv = coefficientOfVariation(data); // -52.25
  const dd = maxDrawdown(data); // 111.80

  const hitScore = (hit / 100) * 10;
  const cvScore = Math.max(0, 10 - cv * 10);
  const ddScore = Math.max(0, 10 - dd / 5);

  const rawScore = hitScore * 0.4 + cvScore * 0.4 + ddScore * 0.2;
  const rounded = Math.round(rawScore);
  return Math.min(10, Math.max(0, rounded));
}

export async function fetchSchemeProsCons(
  payload: SchemeProsConsPayload,
): Promise<SchemeProsConsResponse> {
  const data = await apiPost<SchemeProsConsResponse>("schemeProsCons", payload);

  if (!data) {
    throw new Error("Failed to fetch scheme pros & cons");
  }

  return data;
}

export function calculateAge(
  startDate: Date | string | number | undefined | null,
  endDate: Date | string | number | undefined | null = new Date(),
) {
  if (!startDate || !endDate) return "";
  const start = new Date(startDate);
  const end = new Date(endDate);

  let years = end.getFullYear() - start.getFullYear();
  let months = end.getMonth() - start.getMonth();

  if (months < 0) {
    years--;
    months += 12;
  }

  if (months === 0) {
    return `${years}Y`;
  }

  return `${years}Y ${months}M`;
}

export const formatFileSize = (bytes: number) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(2)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
};

export function getBlogsBadge(id: number, badgeText?: string) {
  switch (id) {
    case 36:
      return { label: "Market News", bgColor: "bg-[#F57C00]" };
    case 31:
      return { label: "MF Expert View", bgColor: "bg-[#1976D2]" };
    case 28:
      return { label: "MF Learning", bgColor: "bg-[#388E3C]" };
    case 30:
      return { label: "MF News", bgColor: "bg-[#F57C00]" };
    case 32:
      return { label: "MF Research", bgColor: "bg-[#CD1F48]" };
    default:
      return { label: badgeText || "Default Badge", bgColor: "bg-[#1976D2]" };
  }
}

const MEDIA_IMAGE_BASE = "https://admin.mysiponline.com/img/media_image/";
const YOUTUBE_EMBED_BASE = "https://www.youtube.com/embed/";

export async function getMediaList() {
  const mediaData = await getNewsMedia();

  const published = mediaData
    .filter((item) => item.status === 1)
    .sort(
      (a, b) =>
        new Date(b.publish_date).getTime() - new Date(a.publish_date).getTime(),
    );

  return published.map((item) => {
    let href: string | null = null;
    let linkType: MediaItem["linkType"] = "none";
    let linkText = "";

    if (item.video_link) {
      href = `${YOUTUBE_EMBED_BASE}${item.video_link}`;
      linkType = "video";
      linkText = "Watch Video";
    } else if (item.link) {
      href = item.link;
      linkType = "external";
      linkText = "Read More";
    }

    return {
      id: item.id,
      title: item.subject,
      content: item.content,
      imageUrl: `${MEDIA_IMAGE_BASE}${item.image}`,
      postedOn: formatDate(item.publish_date, "dd MMM yyyy"),
      href,
      linkType,
      linkText,
    };
  });
}

export function createJobApplicationFormData(
  formData: JobApplicationFormData,
  file: File,
  selectedJob?: JobItem | null,
): FormData {
  const data = new FormData();

  // Candidate details
  data.append("fullName", formData.fullName.trim());
  data.append("email", formData.email.trim());
  data.append("phone", formData.phone.trim());
  data.append("expYears", String(formData.expYears ?? "").trim());
  data.append("expMonths", String(formData.expMonths ?? "").trim());

  const linkedIn = (formData.linkedIn ?? formData.linkedin ?? "").trim();
  if (linkedIn) {
    data.append("linkedIn", linkedIn);
  }

  const coverLetter = (formData.coverLetter ?? "").trim();
  if (coverLetter) {
    data.append("coverLetter", coverLetter);
  }

  // Job details (required by backend validator)
  data.append("jobTitle", selectedJob?.title?.trim() || "");
  data.append("department", selectedJob?.department?.trim() || "");
  data.append("location", selectedJob?.location?.trim() || "");

  // Resume file (PDF, DOC, DOCX up to 5 MB)
  data.append("resume", file);

  return data;
}

export function applyTextFormatting(
  tag: TextFormattingTag,
  textarea: HTMLTextAreaElement | null,
  currentText: string,
  setText: (newText: string) => void,
): void {
  if (!textarea) return;

  const start = textarea.selectionStart;
  const end = textarea.selectionEnd;
  const selectedText = currentText.substring(start, end);
  let replacement = "";

  switch (tag) {
    case "bold":
      replacement = selectedText
        ? `<strong>${selectedText}</strong>`
        : "<strong>text</strong>";
      break;
    case "italic":
      replacement = selectedText
        ? `<em>${selectedText}</em>`
        : "<em>text</em>";
      break;
    case "underline":
      replacement = selectedText ? `<u>${selectedText}</u>` : "<u>text</u>";
      break;
    case "ul":
      replacement = selectedText
        ? `<ul>\n  <li>${selectedText}</li>\n</ul>`
        : "<ul>\n  <li>List item 1</li>\n  <li>List item 2</li>\n</ul>";
      break;
    case "ol":
      replacement = selectedText
        ? `<ol>\n  <li>${selectedText}</li>\n</ol>`
        : "<ol>\n  <li>Step 1</li>\n  <li>Step 2</li>\n</ol>";
      break;
    case "link":
      replacement = selectedText
        ? `<a href="https://">${selectedText}</a>`
        : '<a href="https://">link text</a>';
      break;
  }

  const newContent =
    currentText.substring(0, start) + replacement + currentText.substring(end);
  setText(newContent);

  // Reset focus
  setTimeout(() => {
    textarea.focus();
    const cursorOffset = start + replacement.length;
    textarea.setSelectionRange(cursorOffset, cursorOffset);
  }, 0);
}

// ---------- formatDate ----------

export function formatDate(
  input: number | string | Date | undefined | null,
  formatType?: "dd MMM yyyy" | "MMM dd" | "MMM yyyy" | "yyyy" | "dd-MM-yyyy" | "yyyy-MM-dd" | "dd-MMM-yyyy",
): string {
  if (input === undefined || input === null) return "-";

  let date: Date;

  // Normalize input
  if (typeof input === "string") {
    const parts = input.split("-");
    if (parts.length === 3 && parts[0].length === 2 && parts[1].length === 2 && parts[2].length === 4) {
      // dd-mm-yyyy
      const [day, month, year] = parts;
      date = new Date(`${year}-${month}-${day}T00:00:00+05:30`); // Force IST midnight
    } else {
      date = new Date(input);
    }
  } else {
    date = new Date(input);
  }

  if (isNaN(date.getTime())) return "-";

  // Extract with IST timezone
  const opts = { timeZone: "Asia/Kolkata" } as const;
  const day = date.toLocaleString("en-GB", { day: "2-digit", ...opts });
  const monthShort = date.toLocaleString("en-GB", { month: "short", ...opts });
  const monthLong = date.toLocaleString("en-GB", { month: "long", ...opts });
  const monthNumeric = date.toLocaleString("en-GB", { month: "2-digit", ...opts });
  const year = date.toLocaleString("en-GB", { year: "numeric", ...opts });

  switch (formatType) {
    case "dd-MMM-yyyy":
      return `${day}-${monthShort}-${year}`;
    case "dd MMM yyyy":
      return `${day} ${monthShort} ${year}`;
    case "MMM dd":
      return `${monthShort} ${day}`;
    case "MMM yyyy":
      return `${monthShort} ${year}`;
    case "yyyy":
      return `${year}`;
    case "dd-MM-yyyy":
      return `${day}-${monthNumeric}-${year}`;
    case "yyyy-MM-dd":
      return `${year}-${monthNumeric}-${day}`;
    default:
      return `${monthLong} ${day}, ${year}`;
  }
}

// ---------- formatIndianNum ----------

export function formatIndianNumber(
  num: number | string,
  options?: {
    prefix?: string;
    suffix?: string;
    allowDecimal?: boolean; // 👈 new optional flag
    decimalPlaces?: number; // 👈 control decimal precision
  },
): string {
  let value = typeof num === "string" ? Number(num) : num;

  if (isNaN(value)) return "";

  const {
    prefix = "",
    suffix = "",
    allowDecimal = false,
    decimalPlaces = 2,
  } = options || {};

  const isNegative = value < 0;
  value = Math.abs(value);

  const integerPart = Math.floor(value);
  const decimalPart = allowDecimal ? value - integerPart : 0;

  // Format integer part in Indian style
  const str = integerPart.toString();
  const lastThree = str.slice(-3);
  const otherNumbers = str.slice(0, -3);
  const formattedInt = otherNumbers
    ? otherNumbers.replace(/\B(?=(\d{2})+(?!\d))/g, ",") + "," + lastThree
    : lastThree;

  // Format decimal part (optional)
  let formattedDecimal = "";
  if (allowDecimal && decimalPart > 0) {
    const fixedDecimal = decimalPart.toFixed(decimalPlaces).slice(1); // ".12"
    formattedDecimal = fixedDecimal.replace(/0+$/, ""); // remove trailing zeros
  }

  const sign = isNegative ? "-" : "";
  return `${sign}${prefix}${formattedInt}${formattedDecimal}${suffix}`;
}

export function formatCompactNumber(
  num: number,
  decimalValue = true,
  decimalPlaces = 1,
  suffixShortSymbol = true,
): string {
  const isNegative = num < 0;
  const absNum = Math.abs(num);

  const units = [
    { value: 1000000000, suffix: suffixShortSymbol ? "B" : " Billion" },
    { value: 10000000, suffix: suffixShortSymbol ? " Cr" : " Crore" },
    { value: 100000, suffix: suffixShortSymbol ? " L" : " Lakh" },
    { value: 1000, suffix: suffixShortSymbol ? "k" : " Thousand" },
  ];

  for (const { value, suffix } of units) {
    if (absNum >= value) {
      const formatted = absNum / value;

      const result =
        (decimalValue
          ? formatted.toFixed(decimalPlaces)
          : formatted.toFixed(0)
        ).replace(/\.0+$/, "") + suffix;

      return isNegative ? `-${result}` : result;
    }
  }

  const result = decimalValue
    ? absNum.toFixed(decimalPlaces).replace(/\.0+$/, "")
    : absNum.toFixed(0);

  return isNegative ? `-${result}` : result;
}

export const formatDisplayAmount = (value: number) => {
  return Math.abs(value) >= 100000000 // 1 Crore
    ? "₹" + formatCompactNumber(value, true, 2)
    : formatIndianNumber(value, { prefix: "₹" });
};

// ---------- formatString ----------

export function capitalize(str: string) {
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
}

// ---------- formatTime ----------

export function formatTime(time: string): string {
  const parts = time.split(":").map(Number);
  if (parts.some(isNaN)) return "";

  if (parts.length === 1) {
    return `${parts[0]}s`;
  }

  if (parts.length === 2) {
    const [minutes, seconds] = parts;
    return `${minutes}m ${seconds}s`;
  }

  if (parts.length === 3) {
    const [hours, minutes] = parts;
    return `${hours}h ${minutes}m`;
  }

  return "";
}

// ---------- formatViews ----------

export const formatViews = (num: number): string => {
  if (num < 1000) return num.toString();
  if (num < 1_000_000) return (num / 1_000).toFixed(1).replace(/\.0$/, '') + 'k';
  if (num < 1_000_000_000) return (num / 1_000_000).toFixed(1).replace(/\.0$/, '') + 'M';
  if (num < 1_000_000_000_000) return (num / 1_000_000_000).toFixed(1).replace(/\.0$/, '') + 'B';
  return (num / 1_000_000_000_000).toFixed(1).replace(/\.0$/, '') + 'T';
};

// ---------- getDateDiffFromNow ----------

type DiffFormat =
  | "full" // 5y 2m 10d
  | "year" // 5y
  | "month" // 62m
  | "day" // 1890d
  | "year-month" // 5y 2m
  | "month-day"; // 62m 10d

export function getDateDiffFromNow({
  val,
  format = "full",
  smallSuffix = true,
}: {
  val: string | Date | null;
  format?: DiffFormat;
  smallSuffix?: boolean;
}): string {
  if (!val) return "";
  const date = new Date(val);
  const now = new Date();

  if (isNaN(date.getTime())) return "";

  let years = now.getFullYear() - date.getFullYear();
  let months = now.getMonth() - date.getMonth();
  let days = now.getDate() - date.getDate();

  // Adjust days
  if (days < 0) {
    months -= 1;
    const prevMonth = new Date(now.getFullYear(), now.getMonth(), 0);
    days += prevMonth.getDate();
  }

  // Adjust months
  if (months < 0) {
    years -= 1;
    months += 12;
  }

  const totalMonths = years * 12 + months;
  const totalDays = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));

  switch (format) {
    case "year":
      return smallSuffix ? `${years}y` : `${years} ${years > 1 ? "Years" : "Year"}`;

    case "month":
      return smallSuffix ? `${totalMonths}m` : `${totalMonths} ${totalMonths > 1 ? "Months" : "Month"}`;

    case "day":
      return smallSuffix ? `${totalDays}d` : `${totalDays} ${totalDays > 1 ? "Days" : "Day"}`;

    case "year-month":
      return `${years}y ${months}m`;

    case "month-day":
      return `${totalMonths}m ${days}d`;

    case "full":
    default:
      return `${years}y ${months}m ${days}d`;
  }
}

export const getFundAgeInYears = (fundAge: string | null): number => {
  if (!fundAge) return 0;
  const match = fundAge.match(/\d+/);
  return match ? parseInt(match[0], 10) : 0;
};

// ---------- getYtThumb ----------

export function getYoutubeThumbnail(url: string): string | null {
  try {
    const urlObj = new URL(url);
    const videoId = urlObj.searchParams.get("v");

    if (!videoId) return null;

    return `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;
  } catch (error) {
    console.error("Invalid URL:", url, error);
    return null;
  }
}

export function getYTVideoId(url: string): string | null {
  try {
    const parsedUrl = new URL(url);

    if (parsedUrl.searchParams.has("v")) {
      return parsedUrl.searchParams.get("v");
    }

    if (parsedUrl.hostname === "youtu.be") {
      return parsedUrl.pathname.slice(1);
    }

    const pathParts = parsedUrl.pathname.split("/");
    const candidates = ["embed", "shorts"];
    const idx = pathParts.findIndex((p) => candidates.includes(p));

    if (idx !== -1 && pathParts[idx + 1]) {
      return pathParts[idx + 1];
    }

    return url;
  } catch {
    return url;
  }
}

// ---------- getRelativeTime ----------

export function getRelativeTime(dateInput: string | Date): string {
  const date = new Date(dateInput);
  const now = new Date();
  const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

  if (isNaN(diffInSeconds)) return "";

  const minutes = Math.floor(diffInSeconds / 60);
  const hours = Math.floor(diffInSeconds / 3600);
  const days = Math.floor(diffInSeconds / 86400);
  const months = Math.floor(diffInSeconds / 2592000);
  const years = Math.floor(diffInSeconds / 31536000);

  if (diffInSeconds < 60) return "Just now";
  if (minutes < 60) return `${minutes} minute${minutes > 1 ? "s" : ""} ago`;
  if (hours < 24) return `${hours} hour${hours > 1 ? "s" : ""} ago`;
  if (days < 30) return `${days} day${days > 1 ? "s" : ""} ago`;
  if (months < 12) return `${months} month${months > 1 ? "s" : ""} ago`;
  return `${years} year${years > 1 ? "s" : ""} ago`;
}

// ---------- normalizeRiskCategory ----------

type RiskCategory = "very low" | "low" | "medium" | "moderate to high" | "high" | "very high";

export function normalizeRiskCategory(raw?: string | null): RiskCategory {
  if (!raw || typeof raw !== "string") return "medium";
  const value = raw.trim().toLowerCase();

  if (["low", "very low", "very low risk"].includes(value)) return "very low";
  if (["low to moderate", "moderately low", "moderate low", "low risk"].includes(value)) return "low";
  if (["moderate", "average", "medium", "medium risk"].includes(value)) return "medium";
  if (["moderate high", "moderately high", "moderate to high"].includes(value)) return "moderate to high";
  if (["high", "high risk"].includes(value)) return "high";
  if (["very high", "very high risk"].includes(value)) return "very high";

  return "medium";
}

// ---------- readingTimeCalc ----------

/**
 * Calculate estimated reading time from HTML string.
 * @param htmlString - HTML content as a string
 * @returns Reading time in minutes (rounded up)
 */
export function getReadingTime(htmlString: string): string {
  if (!htmlString) return "";

  const plainText = htmlString.replace(/<[^>]*>/g, " ");
  const words = plainText.trim().split(/\s+/).filter(Boolean);
  const wordCount = words.length;
  const wordsPerMinute = 200;

  const minutes = Math.ceil(wordCount / wordsPerMinute);
  return minutes > 0 ? `${minutes} min read` : "1 min read";
}

// ---------- scrollLock ----------

// This utility manages the scroll lock state of the body element.
// It allows you to lock and unlock the scroll, preventing background scrolling when a modal or overlay is open.
// It uses a counter to keep track of how many times the scroll lock has been activated.
// This way, it only unlocks the scroll when all locks have been released.
// This is useful for scenarios where multiple components might want to lock the scroll at the same time.

// Usage:
// import { lockBodyScroll, unlockBodyScroll } from "@/utils/commonFunctions";

// lockBodyScroll(); // Call this to lock the scroll
// unlockBodyScroll(); // Call this to unlock the scroll 

// useEffect(() => {
//     if (isOpen) {
//       lockBodyScroll();
//     } else {
//       unlockBodyScroll();
//     }
//   }, [isOpen]);

let scrollLockCounter = 0;

export const lockBodyScroll = () => {
  scrollLockCounter += 1;
  if (scrollLockCounter === 1) {
    document.body.style.overflow = 'hidden';
  }
};

export const unlockBodyScroll = () => {
  if (scrollLockCounter > 0) {
    scrollLockCounter -= 1;
    if (scrollLockCounter === 0) {
      document.body.style.overflow = '';
    }
  }
};
